import { useApiMutation, useApiQuery } from "@gladis/api-client";
import type { HouseholdDto, InvitePreviewDto } from "@gladis/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { AuthPage } from "../../components/auth-page.js";
import { apiClient } from "../../lib/api.js";
import { useAuth } from "../../lib/auth-context.js";
import { Button, LoadingState } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";

/**
 * Accepting an invite (FR-025, FR-028).
 *
 * PUBLIC. The invitee may have no account at all — under invite-only signup this link is the only
 * route by which a second account comes into existence — so the page has to render before it knows
 * who, or whether, the visitor is.
 */
export const Route = createFileRoute("/invite/$token")({
  component: InviteRoute,
});

function InviteRoute(): ReactNode {
  const t = useT();
  const { token } = Route.useParams();
  const { isAuthenticated, isRestoring } = useAuth();

  const preview = useApiQuery<InvitePreviewDto>(["invite", token], `/invites/${token}`);

  if (isRestoring || preview.isLoading) return <Checking />;

  if (preview.isError) {
    // 410 expired, 409 already used. Both mean the same thing to the person holding the link, and
    // the distinction that matters — "ask for a new one" — is in the message.
    return (
      <AuthPage>
        <h1 className="text-2xl font-semibold tracking-tight">{t("invite.invalid")}</h1>
        <p className="mt-2 text-sm text-muted">{errorSentence(preview.error)}</p>
        <p className="mt-2 text-sm text-muted">{t("invite.askForNew")}</p>
      </AuthPage>
    );
  }

  const invite = preview.data;
  if (!invite) return <Checking />;

  if (!isAuthenticated) return <SignInToAccept invite={invite} token={token} />;
  return <AcceptAsCurrentUser invite={invite} token={token} />;
}

/**
 * The invitee is not signed in.
 *
 * Sending them through the ordinary magic-link flow keeps ONE authentication path in the system.
 * Provisioning happens server-side when they accept — this screen only gets them a session.
 */
function SignInToAccept({ invite, token }: { invite: InvitePreviewDto; token: string }): ReactNode {
  const t = useT();
  const [sent, setSent] = useState(false);

  // The CLAIM endpoint, not sign-in. Sign-in refuses to provision (FR-014), so an invitee with no
  // account would get no email at all — the flow would dead-end for exactly the people it is for.
  const request = useApiMutation<void, void>((client) => client.post(`/invites/${token}/claim`));

  if (sent) {
    return (
      <AuthPage key="sent">
        <h1 className="text-2xl font-semibold tracking-tight">{t("auth.checkYourEmail")}</h1>
        <p className="mt-2 text-sm text-muted">
          We&apos;ve sent a sign-in link to {invite.email}. Follow it, then open this invite again
          to join {invite.householdName}.
        </p>
      </AuthPage>
    );
  }

  return (
    <AuthPage key="ask">
      <h1 className="text-2xl font-semibold tracking-tight">Join {invite.householdName}</h1>
      <p className="mt-2 text-sm text-muted">
        {invite.inviterDisplayName} invited {invite.email} to share their household finances.
      </p>
      <Button
        variant="primary"
        className="mt-8 w-full"
        type="button"
        onClick={() => {
          request.mutate();
          setSent(true);
        }}
      >
        {t("invite.emailMeALink")}
      </Button>
    </AuthPage>
  );
}

/**
 * Someone IS signed in.
 *
 * FR-028: if that is not the person the invite was addressed to, the choice is made explicit and
 * the link is NOT silently consumed. Accepting under the wrong account would quietly burn a
 * single-use invite and put the wrong person in the household — and it is an easy mistake, because
 * a household shares a computer.
 */
function AcceptAsCurrentUser({
  invite,
  token,
}: {
  invite: InvitePreviewDto;
  token: string;
}): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const { clear } = useAuth();

  const me = useApiQuery<{ user: { email: string; displayName: string } }>(["me"], "/me");

  const accept = useApiMutation<HouseholdDto, void>(
    (client) => client.post<HouseholdDto>(`/invites/${token}/accept`),
    {
      invalidates: [["me"]],
      onSuccess: () => void navigate({ to: "/", replace: true }),
    },
  );

  async function signOutAndRetry(): Promise<void> {
    await apiClient.post("/auth/sign-out");
    clear();
    window.location.reload();
  }

  const signedInAs = me.data?.user;
  const addressedToSomeoneElse =
    signedInAs !== undefined && signedInAs.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <AuthPage>
      <h1 className="text-2xl font-semibold tracking-tight">Join {invite.householdName}</h1>

      {addressedToSomeoneElse ? (
        <p className="mt-2 text-sm text-muted" role="alert">
          This invitation was sent to {invite.email}, but you&apos;re signed in as{" "}
          {signedInAs.email}. You can join as {signedInAs.displayName}, or sign out and use the
          invited address.
        </p>
      ) : (
        <p className="mt-2 text-sm text-muted">
          {invite.inviterDisplayName} invited you to share their household finances.
        </p>
      )}

      <Button
        variant="primary"
        className="mt-8 w-full"
        type="button"
        onClick={() => accept.mutate()}
        disabled={accept.isPending}
      >
        {accept.isPending
          ? t("invite.joining")
          : addressedToSomeoneElse
            ? `Join as ${signedInAs.displayName}`
            : t("invite.accept")}
      </Button>

      <Button className="mt-3 w-full" type="button" onClick={() => void signOutAndRetry()}>
        {t("invite.useDifferentAccount")}
      </Button>

      {accept.isError ? (
        <p className="mt-4 text-sm text-muted" role="alert">
          {errorSentence(accept.error)}
        </p>
      ) : null}
    </AuthPage>
  );
}

/** The one wait this page has. Shared so both of its call sites cannot drift apart. */
function Checking(): ReactNode {
  const t = useT();
  return (
    <AuthPage>
      <LoadingState>{t("invite.checking")}</LoadingState>
    </AuthPage>
  );
}
